import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { useEffect } from 'react';

/**
 * Initialize Sentry error monitoring
 * Only runs in production or when explicitly enabled
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
  const isProduction = import.meta.env.PROD;
  const enableSentry = import.meta.env.VITE_ENABLE_SENTRY === 'true';

  // Only initialize Sentry if DSN is provided and either in production or explicitly enabled
  if (!dsn || (!isProduction && !enableSentry)) {
    console.log('[Sentry] Not initialized - missing DSN or not in production');
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Performance Monitoring
    integrations: [
      // React Router integration for automatic route tracking
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Replay integration for session replay
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance monitoring sample rate
    // 1.0 = 100% of transactions are sent to Sentry
    // Adjust based on traffic volume and budget
    tracesSampleRate: isProduction ? 0.1 : 1.0,

    // Session Replay sample rate
    // This sets the sample rate for normal sessions (no errors)
    replaysSessionSampleRate: 0.1,

    // This sets the sample rate for sessions with errors
    replaysOnErrorSampleRate: 1.0,

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'development',

    // Filter out sensitive information
    beforeSend(event) {
      // Don't send events in development unless explicitly enabled
      if (!isProduction && !enableSentry) {
        return null;
      }

      // Filter out non-error console logs
      if (event.level === 'log' || event.level === 'info') {
        return null;
      }

      // Sanitize sensitive data from error messages
      if (event.message) {
        event.message = sanitizeMessage(event.message);
      }

      if (event.exception?.values) {
        event.exception.values = event.exception.values.map(exception => ({
          ...exception,
          value: exception.value ? sanitizeMessage(exception.value) : exception.value,
        }));
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      // ResizeObserver errors (common and harmless)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Development-only errors
      'HMR',
      'vite',
    ],

    // Ignore errors from certain URLs
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  console.log(`[Sentry] Initialized in ${environment} environment`);
};

/**
 * Sanitize sensitive information from error messages
 */
function sanitizeMessage(message: string): string {
  return message
    // Remove email addresses
    .replace(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi, '[EMAIL]')
    // Remove tokens and API keys
    .replace(/Bearer\s+[A-Za-z0-9-._~+/]+=*/gi, 'Bearer [TOKEN]')
    .replace(/api[_-]?key[=:]\s*[A-Za-z0-9-._~+/]+=*/gi, 'api_key=[KEY]')
    // Remove UUIDs
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '[UUID]')
    // Remove phone numbers
    .replace(/(\+\d{1,3}[- ]?)?\d{10}/g, '[PHONE]');
}

/**
 * Manually capture an exception
 */
export const captureException = (error: Error, context?: Record<string, unknown>) => {
  if (context) {
    Sentry.setContext('additional', context);
  }
  Sentry.captureException(error);
};

/**
 * Manually capture a message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Set user context for error tracking
 */
export const setUser = (user: { id: string; email?: string; username?: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Add breadcrumb for debugging context
 */
export const addBreadcrumb = (breadcrumb: {
  message: string;
  category?: string;
  level?: Sentry.SeverityLevel;
  data?: Record<string, unknown>;
}) => {
  Sentry.addBreadcrumb(breadcrumb);
};

// Export Sentry for direct use if needed
export { Sentry };
